import { netPnl, tradingDayKey, currentTradingDay } from './pnl';

// Prop firm presets. Traders can change any number afterwards.
// Prop firm presets, grouped by firm. Rules change often, so every number stays
// editable and the editor reminds traders to confirm with their firm.
// Last checked: October 2026 (firm help centers and published rule guides).
export const PRESETS_CHECKED = 'October 2026';

const SIZES = [25000, 50000, 100000, 150000];
const k = (n) => `${n / 1000}K`;

function series(firm, plan, base, rows) {
  return rows.map((r) => ({
    id: `${firm}-${plan}-${k(r.size)}`.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    firm,
    plan,
    label: `${firm} ${k(r.size)} ${plan}`.trim(),
    ...base,
    ...r,
  }));
}

// Topstep: Combine consistency target 55% of total profit; funded (XFA)
// standard payouts need 5 winning days of $150+. Trailing stops at the starting balance.
const TOPSTEP_COMBINE = { platform: 'TopstepX', type: 'Evaluation', drawdownType: 'trailing_eod', consistencyLimit: 55, consistencyBase: 'total' };
const TOPSTEP_XFA = { platform: 'TopstepX', type: 'Funded', drawdownType: 'trailing_eod', payoutWinningDays: 5, payoutMinDayProfit: 150 };

export const PRESETS = [
  ...series('Topstep', 'Combine', TOPSTEP_COMBINE, [
    { size: 50000, profitTarget: 3000, maxLossLimit: 2000 },
    { size: 100000, profitTarget: 6000, maxLossLimit: 3000 },
    { size: 150000, profitTarget: 9000, maxLossLimit: 4500 },
  ]),
  { id: 'topstep-50k-static-combine', firm: 'Topstep', plan: 'Static Combine', label: 'Topstep 50K Static Combine', ...TOPSTEP_COMBINE, drawdownType: 'static', size: 50000, profitTarget: 4000, maxLossLimit: 2000 },
  ...series('Topstep', 'Funded (XFA)', TOPSTEP_XFA, [
    { size: 50000, maxLossLimit: 2000 },
    { size: 100000, maxLossLimit: 3000 },
    { size: 150000, maxLossLimit: 4500 },
  ]),

  // Apex: evaluation has no consistency rule; trailing stops at start + $100.
  // EOD plans add a daily loss limit; intraday plans trail in real time.
  ...series('Apex', 'EOD Evaluation', { type: 'Evaluation', drawdownType: 'trailing_eod', trailLockOffset: 100 },
    SIZES.map((size, i) => ({ size, profitTarget: [1500, 3000, 6000, 9000][i], maxLossLimit: [1000, 2000, 3000, 4000][i], dailyLossLimit: [500, 1000, 1500, 2000][i] }))),
  ...series('Apex', 'Intraday Evaluation', { type: 'Evaluation', drawdownType: 'trailing_intraday', trailLockOffset: 100 },
    SIZES.map((size, i) => ({ size, profitTarget: [1500, 3000, 6000, 9000][i], maxLossLimit: [1000, 2000, 3000, 4000][i] }))),

  // MyFundedFutures: end-of-day trailing evaluations with a 50% consistency rule.
  ...series('MyFundedFutures', 'Rapid', { type: 'Evaluation', drawdownType: 'trailing_eod', consistencyLimit: 50, consistencyBase: 'total' },
    SIZES.map((size, i) => ({ size, profitTarget: [1500, 3000, 6000, 9000][i], maxLossLimit: [1000, 2000, 3000, 4500][i] }))),
  ...series('MyFundedFutures', 'Pro', { type: 'Evaluation', drawdownType: 'trailing_eod', consistencyLimit: 50, consistencyBase: 'total' },
    [50000, 100000, 150000].map((size, i) => ({ size, profitTarget: [3000, 6000, 9000][i], maxLossLimit: [2000, 3000, 4500][i] }))),

  // Lucid Trading (checked October 2026): every plan trails end-of-day and stops at the starting
  // balance plus $100. The daily loss limit is optional at checkout on Pro, Flex and Daily, so it's
  // left off; add it if you bought it ($600 / $1,200 / $1,800 / $2,700). Flex passes with no day over
  // 40% of total profit, Daily with 50%; Pro has no consistency rule until funded (40% for payouts).
  // Direct is funded from day one: a daily loss limit from 50K up and 20% consistency for payouts.
  // `brand`: the plan's first word is Lucid's own name, kept in English (Daily, Direct).
  ...series('Lucid', 'Pro', { brand: true, type: 'Evaluation', drawdownType: 'trailing_eod', trailLockOffset: 100 },
    SIZES.map((size, i) => ({ size, profitTarget: [1250, 3000, 6000, 9000][i], maxLossLimit: [1000, 2000, 3000, 4500][i] }))),
  ...series('Lucid', 'Flex', { brand: true, type: 'Evaluation', drawdownType: 'trailing_eod', trailLockOffset: 100, consistencyLimit: 40, consistencyBase: 'total' },
    SIZES.map((size, i) => ({ size, profitTarget: [1250, 3000, 6000, 9000][i], maxLossLimit: [1000, 2000, 3000, 4500][i] }))),
  ...series('Lucid', 'Daily', { brand: true, type: 'Evaluation', drawdownType: 'trailing_eod', trailLockOffset: 100, consistencyLimit: 50, consistencyBase: 'total' },
    SIZES.map((size, i) => ({ size, profitTarget: [1250, 3000, 6000, 9000][i], maxLossLimit: [1000, 2000, 3000, 4500][i] }))),
  ...series('Lucid', 'Daily Intraday', { brand: true, type: 'Evaluation', drawdownType: 'trailing_intraday', trailLockOffset: 100, consistencyLimit: 50, consistencyBase: 'total' },
    SIZES.map((size, i) => ({ size, profitTarget: [1250, 3000, 6000, 9000][i], maxLossLimit: [1000, 2000, 3000, 4500][i] }))),
  ...series('Lucid', 'Pro Funded', { brand: true, type: 'Funded', drawdownType: 'trailing_eod', trailLockOffset: 100, consistencyLimit: 40, consistencyBase: 'total' },
    SIZES.map((size, i) => ({ size, maxLossLimit: [1000, 2000, 3000, 4500][i] }))),
  ...series('Lucid', 'Flex Funded', { brand: true, type: 'Funded', drawdownType: 'trailing_eod', trailLockOffset: 100, payoutWinningDays: 5 },
    SIZES.map((size, i) => ({ size, maxLossLimit: [1000, 2000, 3000, 4500][i], payoutMinDayProfit: [100, 150, 200, 250][i] }))),
  ...series('Lucid', 'Daily Funded', { brand: true, type: 'Funded', drawdownType: 'trailing_intraday', trailLockOffset: 100 },
    SIZES.map((size, i) => ({ size, maxLossLimit: [1000, 2000, 3000, 4500][i] }))),
  ...series('Lucid', 'Direct', { brand: true, type: 'Funded', drawdownType: 'trailing_eod', trailLockOffset: 100, consistencyLimit: 20, consistencyBase: 'total' },
    SIZES.map((size, i) => ({ size, maxLossLimit: [1000, 2000, 3500, 5000][i], ...(i ? { dailyLossLimit: [0, 1200, 2100, 3000][i] } : {}) }))),

  // Tradeify: Growth evaluation (from Tradeify's help center), end-of-day trailing,
  // daily loss limit, no consistency rule.
  ...series('Tradeify', 'Growth', { type: 'Evaluation', drawdownType: 'trailing_eod' },
    SIZES.map((size, i) => ({ size, profitTarget: [1500, 3000, 6000, 9000][i], maxLossLimit: [1000, 2000, 3500, 5000][i], dailyLossLimit: [600, 1250, 2500, 3750][i] }))),

  // TradeDay: 6% targets, end-of-day trailing, no daily limit, and a 30% consistency
  // cap measured against the profit target.
  ...series('TradeDay', 'Evaluation', { type: 'Evaluation', drawdownType: 'trailing_eod', consistencyLimit: 30, consistencyBase: 'target' },
    [50000, 100000, 150000].map((size, i) => ({ size, profitTarget: [3000, 6000, 9000][i], maxLossLimit: [2000, 3000, 4500][i] }))),

  // Take Profit Trader: monthly Test, end-of-day trailing that stops at the starting balance,
  // no daily loss limit, best day under 50% of total profit. The PRO (funded) account trails
  // in real time with the same max loss.
  ...series('Take Profit Trader', 'Test', { type: 'Evaluation', drawdownType: 'trailing_eod', consistencyLimit: 50, consistencyBase: 'total' },
    [25000, 50000, 75000, 100000, 150000].map((size, i) => ({ size, profitTarget: [1500, 3000, 4500, 6000, 9000][i], maxLossLimit: [1500, 2000, 2500, 3000, 4500][i] }))),
  ...series('Take Profit Trader', 'PRO', { type: 'Funded', drawdownType: 'trailing_intraday' },
    [25000, 50000, 75000, 100000, 150000].map((size, i) => ({ size, maxLossLimit: [1500, 2000, 2500, 3000, 4500][i] }))),

  // Alpha Futures: every plan trails end-of-day and stops at the starting balance. Zero has a
  // daily loss guard and no consistency rule; Standard 50% and Advanced 40% of total profit.
  ...series('Alpha Futures', 'Zero', { type: 'Evaluation', drawdownType: 'trailing_eod' },
    [25000, 50000, 100000].map((size, i) => ({ size, profitTarget: [1500, 3000, 6000][i], maxLossLimit: [1000, 2000, 3000][i], dailyLossLimit: [500, 1000, 2000][i] }))),
  ...series('Alpha Futures', 'Standard', { type: 'Evaluation', drawdownType: 'trailing_eod', consistencyLimit: 50, consistencyBase: 'total' },
    [50000, 100000, 150000].map((size, i) => ({ size, profitTarget: [3000, 6000, 9000][i], maxLossLimit: [2000, 3000, 4500][i] }))),
  ...series('Alpha Futures', 'Advanced', { type: 'Evaluation', drawdownType: 'trailing_eod', consistencyLimit: 40, consistencyBase: 'total' },
    [50000, 100000, 150000].map((size, i) => ({ size, profitTarget: [4000, 8000, 12000][i], maxLossLimit: [1750, 3500, 5250][i] }))),

  // Earn2Trade: Trader Career Path and Gauntlet Mini, end-of-day trailing that stops at the
  // starting balance, a daily loss limit, and no day 30% or more of total profit.
  ...series('Earn2Trade', 'Trader Career Path', { type: 'Evaluation', drawdownType: 'trailing_eod', consistencyLimit: 30, consistencyBase: 'total' },
    [25000, 50000, 100000].map((size, i) => ({ size, profitTarget: [1750, 3000, 6000][i], maxLossLimit: [1500, 2000, 3500][i], dailyLossLimit: [550, 1100, 2200][i] }))),
  ...series('Earn2Trade', 'Gauntlet Mini', { type: 'Evaluation', drawdownType: 'trailing_eod', consistencyLimit: 30, consistencyBase: 'total' },
    [50000, 100000, 150000, 200000].map((size, i) => ({ size, profitTarget: [3000, 6000, 9000, 11000][i], maxLossLimit: [2000, 3500, 4500, 6000][i], dailyLossLimit: [1100, 2200, 3300, 4400][i] }))),

  // Bulenox: one-time Qualification, no consistency rule. Option 1 trails in real time with no
  // daily limit; Option 2 trails end-of-day with a daily loss limit. The Master (funded)
  // account keeps the option and stops trailing at the starting balance plus $100.
  ...series('Bulenox', 'Option 1', { type: 'Evaluation', drawdownType: 'trailing_intraday' },
    SIZES.map((size, i) => ({ size, profitTarget: [1500, 3000, 6000, 9000][i], maxLossLimit: [1500, 2500, 3000, 4500][i] }))),
  ...series('Bulenox', 'Option 2', { type: 'Evaluation', drawdownType: 'trailing_eod' },
    SIZES.map((size, i) => ({ size, profitTarget: [1500, 3000, 6000, 9000][i], maxLossLimit: [1500, 2500, 3000, 4500][i], dailyLossLimit: [500, 1100, 2200, 3300][i] }))),
  ...series('Bulenox', 'Master Option 1', { type: 'Funded', drawdownType: 'trailing_intraday', trailLockOffset: 100 },
    SIZES.map((size, i) => ({ size, maxLossLimit: [1500, 2500, 3000, 4500][i] }))),
  ...series('Bulenox', 'Master Option 2', { type: 'Funded', drawdownType: 'trailing_eod', trailLockOffset: 100 },
    SIZES.map((size, i) => ({ size, maxLossLimit: [1500, 2500, 3000, 4500][i], dailyLossLimit: [500, 1100, 2200, 3300][i] }))),
];

export const PRESET_FIRMS = [...new Set(PRESETS.map((p) => p.firm))];

// How each firm is found in the firm search (FirmPicker.js): its full name and the names
// traders also type.
export const FIRM_INFO = {
  Topstep: { name: 'Topstep', aliases: ['TopstepX', 'Top Step'] },
  Apex: { name: 'Apex Trader Funding', aliases: ['Apex', 'ATF'] },
  MyFundedFutures: { name: 'MyFundedFutures', aliases: ['MFF', 'MFFU', 'My Funded Futures'] },
  Lucid: { name: 'Lucid Trading', aliases: ['Lucid', 'LucidFlex', 'LucidPro', 'LucidDaily', 'LucidDirect'] },
  Tradeify: { name: 'Tradeify', aliases: [] },
  TradeDay: { name: 'TradeDay', aliases: ['Trade Day'] },
  'Take Profit Trader': { name: 'Take Profit Trader', aliases: ['TPT', 'TakeProfitTrader', 'TakeProfit'] },
  'Alpha Futures': { name: 'Alpha Futures', aliases: ['Alpha'] },
  Earn2Trade: { name: 'Earn2Trade', aliases: ['E2T', 'Earn 2 Trade'] },
  Bulenox: { name: 'Bulenox', aliases: [] },
};

// Firms that run on a platform, for platforms that only some firms use (checked October 2026).
// Other platforms list every firm.
export const PLATFORM_FIRMS = {
  TopstepX: ['Topstep'],
  Tradesea: ['Lucid', 'Tradeify'],
};
export const firmsForPlatform = (platform) => PLATFORM_FIRMS[platform] || PRESET_FIRMS;

const RISK_ORDER = ['safe', 'warning', 'danger', 'blown'];
function worse(a, b) {
  return RISK_ORDER.indexOf(b) > RISK_ORDER.indexOf(a) ? b : a;
}

// Balance and prop-firm distances for one account, from its trades.
// Returns null when the account has no starting balance yet.
// Deposits add to the balance and withdrawals (or payouts) take from it; neither is trading P&L.
// A balance adjustment (personal accounts) carries its own sign and isn't money in or out either.
export function cashFlowAmount(flow) {
  return flow.kind === 'deposit' || flow.kind === 'adjustment' ? flow.amount : -flow.amount;
}

function cashTotals(account, upToKey = null) {
  let deposits = 0;
  let withdrawals = 0;
  let adjusted = 0;
  let lastWithdrawal = null;
  for (const flow of account.cashFlows || []) {
    if (upToKey && flow.date > upToKey) continue;
    if (flow.kind === 'deposit') deposits += flow.amount;
    else if (flow.kind === 'adjustment') adjusted += flow.amount;
    else {
      withdrawals += flow.amount;
      if (!lastWithdrawal || flow.date > lastWithdrawal) lastWithdrawal = flow.date;
    }
  }
  return { deposits, withdrawals, adjusted, net: deposits - withdrawals + adjusted, lastWithdrawal };
}

export function accountStatus(account, trades) {
  const start = account.size;
  if (!start) return null;

  const byDay = {};
  for (const t of trades) {
    const key = tradingDayKey(t.date);
    byDay[key] = (byDay[key] || 0) + netPnl(t);
  }
  const days = Object.keys(byDay).sort();
  const today = currentTradingDay();

  // Walk the account forward. A trailing max loss level rises with each new high
  // (end-of-day highs, or every closed trade for intraday trailing) and stops
  // rising once it reaches the starting balance plus the lock offset.
  let balance = start;
  const mll = account.maxLossLimit || 0;
  const lock = start + (account.trailLockOffset || 0);
  let floor = mll ? start - mll : null;
  if (mll && account.drawdownType === 'trailing_intraday') {
    // Closed trades only: open-trade peaks aren't in the CSV, so the real level may be higher.
    const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
    for (const t of sorted) {
      balance += netPnl(t);
      floor = Math.min(lock, Math.max(floor, balance - mll));
    }
  } else {
    for (const key of days) {
      balance += byDay[key];
      if (mll && account.drawdownType !== 'static' && key !== today) {
        floor = Math.min(lock, Math.max(floor, balance - mll));
      }
    }
  }

  // `balance` so far is from trades alone. Cash moved in or out changes what's in the account,
  // and so the room above the max loss level, but not the trailing high: a payout leaves the
  // max loss level where it was (firms differ; this is the cautious reading).
  const cash = cashTotals(account);
  const tradingBalance = balance;
  balance = tradingBalance + cash.net;
  const status = { start, balance, change: tradingBalance - start, todayNet: byDay[today] || 0 };
  if (cash.deposits || cash.withdrawals || cash.adjusted) status.cash = cash;
  let overall = 'safe';
  let tracked = false;
  if (mll) {
    status.floor = floor;
    status.toBlow = balance - floor;
    const ratio = status.toBlow / mll;
    status.risk = status.toBlow <= 0 ? 'blown' : ratio < 0.25 ? 'danger' : ratio < 0.5 ? 'warning' : 'safe';
    overall = worse(overall, status.risk);
    tracked = true;
  }

  // Consistency: best day as a % of total profit (Topstep-style; going over raises
  // the profit target to bestDay ÷ limit) or as a % of the profit target (TradeDay-style).
  let profitTarget = account.profitTarget;
  if (account.consistencyLimit) {
    const bestDay = Math.max(0, ...days.map((key) => byDay[key]));
    const limit = account.consistencyLimit / 100;
    const ofTarget = account.consistencyBase === 'target' && account.profitTarget;
    status.bestDay = bestDay;
    status.consistencyLimit = account.consistencyLimit;
    status.consistencyBase = ofTarget ? 'target' : 'total';
    status.consistencyPct = ofTarget
      ? (bestDay > 0 ? (bestDay / account.profitTarget) * 100 : null)
      : (status.change > 0 && bestDay > 0 ? (bestDay / status.change) * 100 : null);
    status.consistencyOk = status.consistencyPct === null || status.consistencyPct <= account.consistencyLimit;
    if (!ofTarget && profitTarget && bestDay / limit > profitTarget) {
      status.raisedTarget = true;
      profitTarget = bestDay / limit;
    }
    if (!status.consistencyOk) overall = worse(overall, 'warning');
    tracked = true;
  }
  if (profitTarget) {
    status.target = start + profitTarget;
    status.toPass = profitTarget - status.change;
    status.progress = Math.max(0, Math.min(1, status.change / profitTarget));
  }
  if (account.dailyLossLimit) {
    status.dailyLeft = account.dailyLossLimit + Math.min(0, status.todayNet);
    status.dailyRisk = status.dailyLeft <= 0 ? 'danger' : status.dailyLeft < account.dailyLossLimit * 0.25 ? 'warning' : 'safe';
    overall = worse(overall, status.dailyRisk);
    tracked = true;
  }
  if (account.payoutWinningDays) {
    const min = account.payoutMinDayProfit || 0.01;
    // Winning days count again from zero after each payout.
    status.payoutDays = days.filter((k) => byDay[k] >= min && (!cash.lastWithdrawal || k > cash.lastWithdrawal)).length;
    status.payoutNeeded = account.payoutWinningDays;
    status.payoutMin = account.payoutMinDayProfit || 0;
  }
  if (tracked) status.overall = overall;
  return status;
}

// End-of-day balance on a given day (YYYY-MM-DD), counting every trade and every deposit
// or withdrawal up to and including it.
export function balanceOnDay(account, trades, key) {
  if (!account || !account.size) return null;
  const traded = trades.reduce((sum, t) => (tradingDayKey(t.date) <= key ? sum + netPnl(t) : sum), account.size);
  return traded + cashTotals(account, key).net;
}
